// Coach MARK (/app/mark): the first-visit questions about how the trader likes to be coached,
// the end-of-day debrief, what MARK knows about them (editable), and the chat. The server side
// is mark.js; what MARK reads is built by markSnapshot.js.
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';
import { sessionId } from './analytics';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildMarkSnapshot, latestTradingDay, tradeMove } from './markSnapshot';
import { MONEY, chartRows, moneyTone, parseReply } from './markBlocks';
import PeriodPanels from './PeriodPanels';
import { currentTradingDay, formatMoney, tradingDayKey } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

const QUESTIONS = [
  ['tone', 'How should I talk to you?', [['direct', 'Straight and blunt'], ['balanced', 'Honest but encouraging'], ['gentle', 'Gentle and supportive']]],
  ['length', 'How long should my answers be?', [['short', 'Short, get to the point'], ['medium', 'Somewhere in between'], ['detailed', 'Detailed, explain everything']]],
  ['learn', 'What helps you learn best?', [['numbers', 'Numbers and stats'], ['examples', 'Examples from my own trades'], ['steps', 'Clear step-by-step actions']]],
  ['experience', 'How long have you been trading?', [['new', 'Under a year'], ['some', '1 to 3 years'], ['experienced', 'More than 3 years']]],
];
const STARTERS = ['What’s my biggest leak right now?', 'How did I do this week?', 'Am I following my rules?', 'What should I work on in my next session?'];
// A full review reads everything and writes a long, structured answer, so it uses more MARK
// credits (each answer uses what it actually cost; see mark.js). Questions that sound like one get
// asked first which kind of answer to give.
const REVIEW_RANGE = '20–35';
const BIG = /\b(all|every|everything|whole|entire|full|complete|overall)\b.*\b(trades?|data|journal|habits?|trading|review|analysis)\b|\b(review|analy[sz]e|improve) (all|everything|my trading)\b|\b(tod[oa]s?|completo|completa|entero)\b.*\b(operaciones|datos|diario|hábitos|trading)\b|\b(analiza|revisa|mejorar) (todo|mi trading)\b/i;
export const soundsBig = (text) => BIG.test(String(text || ''));
const USAGE_MARK = '\u001e';

const dayText = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'long', month: 'long', day: 'numeric' });
const shortDay = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'short', month: 'short', day: 'numeric' });

// MARK's replies: headings, lists, **bold**, callouts (> ) and dividers, with money in green or
// red, plus the cards, charts and follow-up buttons from markBlocks.js. No HTML is ever used.
function Inline({ text }) {
  return String(text).split(/(\*\*[^*]+\*\*)/).map((part, i) => {
    const bold = part.startsWith('**') && part.endsWith('**') && part.length > 4;
    const body = (bold ? part.slice(2, -2) : part).split(MONEY).map((bit, j) => (j % 2 ? <span key={j} className={`mk-money ${moneyTone(bit)}`}>{bit}</span> : <Fragment key={j}>{bit}</Fragment>));
    return bold ? <strong key={i}>{body}</strong> : <Fragment key={i}>{body}</Fragment>;
  });
}

const BULLET = /^\s*[-*•]\s+/;
const NUMBERED = /^\s*\d+[.)]\s+/;
export function Markdown({ text }) {
  const out = [];
  let list = null;
  let para = [];
  const flushPara = () => { if (para.length) out.push(<p key={out.length}>{para.map((l, j) => <Fragment key={j}>{j > 0 && <br />}<Inline text={l} /></Fragment>)}</p>); para = []; };
  const flushList = () => {
    if (list) out.push(list.ordered ? <ol key={out.length}>{list.items.map((x, j) => <li key={j}><Inline text={x} /></li>)}</ol> : <ul key={out.length}>{list.items.map((x, j) => <li key={j}><Inline text={x} /></li>)}</ul>);
    list = null;
  };
  for (const raw of String(text || '').split('\n')) {
    const line = raw.trimEnd();
    if (!line.trim()) { flushPara(); flushList(); continue; }
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      flushPara(); flushList();
      const H = heading[1].length <= 2 ? 'h3' : 'h4';
      out.push(<H key={out.length} className="mk-h"><Inline text={heading[2].replace(/\*\*/g, '')} /></H>);
    } else if (/^\s*(---+|\*\*\*+)\s*$/.test(line)) {
      flushPara(); flushList();
      out.push(<hr key={out.length} className="mk-rule" />);
    } else if (/^>\s?/.test(line)) {
      flushPara(); flushList();
      const quote = line.replace(/^>\s?/, '');
      const prev = out[out.length - 1];
      if (prev && prev.props && prev.props.className === 'mk-callout') out[out.length - 1] = <div key={prev.key} className="mk-callout">{prev.props.children}<p><Inline text={quote} /></p></div>;
      else out.push(<div key={out.length} className="mk-callout"><p><Inline text={quote} /></p></div>);
    } else if (BULLET.test(line) || NUMBERED.test(line)) {
      flushPara();
      const ordered = NUMBERED.test(line);
      if (list && list.ordered !== ordered) flushList();
      if (!list) list = { ordered, items: [] };
      list.items.push(line.replace(ordered ? NUMBERED : BULLET, ''));
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara(); flushList();
  return out;
}

function Cards({ cards }) {
  return (
    <div className="mk-cards">
      {cards.map((c, i) => (
        <div key={i} className="mk-stat-card">
          {c.title && <h5><Inline text={c.title} /></h5>}
          <dl>{c.rows.slice(0, 8).map((r, j) => Array.isArray(r) && <Fragment key={j}><dt>{String(r[0])}</dt><dd><Inline text={String(r[1])} /></dd></Fragment>)}</dl>
        </div>
      ))}
    </div>
  );
}

const MEASURE_TEXT = { net: 'Net P&L', winRate: 'Win rate', expectancy: 'Average per trade', count: 'Trades' };
// Over time (by month, week or day) MARK's chart is the stacked overview: P&L, win rate, trades.
function Chart({ spec, data }) {
  if (['month', 'week', 'day'].includes(spec && spec.by)) {
    return <PeriodPanels trades={data.trades} unit={spec.by} title={spec.title ? String(spec.title) : undefined} />;
  }
  return <BarChart spec={spec} data={data} />;
}

function BarChart({ spec, data }) {
  const { t } = useI18n();
  const c = useMemo(() => chartRows(data.trades, spec, data), [spec, data]);
  if (!c) return null;
  const fmt = (v) => (c.measure === 'winRate' ? `${Math.round(v)}%` : c.measure === 'count' ? String(v) : formatMoney(v));
  const max = Math.max(...c.rows.map((r) => Math.abs(c.measure === 'winRate' ? r.value - 50 : r.value)), 1);
  const signed = c.measure === 'net' || c.measure === 'expectancy';
  return (
    <figure className="mk-chart">
      <figcaption><strong>{spec.title ? String(spec.title) : t(MEASURE_TEXT[c.measure])}</strong> <small>{t('From your journal')}</small></figcaption>
      <ul>
        {c.rows.map((r) => {
          const tone = signed ? (r.value > 0 ? 'gain' : r.value < 0 ? 'loss' : '') : c.measure === 'winRate' ? (r.value >= 50 ? 'gain' : 'loss') : '';
          const width = c.measure === 'count' ? (r.value / Math.max(...c.rows.map((x) => x.value))) * 100 : c.measure === 'winRate' ? r.value : (Math.abs(r.value) / max) * 100;
          return (
            <li key={r.label} title={t('{n} trades', { n: r.count })}>
              <span className="mk-chart-label">{r.label}</span>
              <span className="mk-chart-track"><span className={`mk-chart-bar ${tone}`} style={{ width: `${Math.max(2, width)}%` }} /></span>
              <span className={`mk-chart-value ${tone}`}>{fmt(r.value)}</span>
            </li>
          );
        })}
      </ul>
    </figure>
  );
}

// Pages a follow-up button may open.
const PAGES = { rules: '/app/morning', playbook: '/app/playbook', insights: '/app/insights', behavior: '/app/insights?tab=behavior', notebook: '/app/notebook', accounts: '/app/accounts', reflection: '/app/reflection', goals: '/app/goals', edge: '/app/journey/edge-milestones', trades: '/app' };
function Next({ items, onAsk, disabled }) {
  const navigate = useNavigate();
  return (
    <div className="mk-next">
      {items.map((x, i) => {
        const label = typeof x === 'string' ? x : x && x.label;
        if (!label) return null;
        const page = x && PAGES[x.page];
        return page
          ? <button key={i} type="button" className="secondary small" onClick={() => navigate(page)}>{label} &rarr;</button>
          : <button key={i} type="button" className="secondary small" disabled={disabled} onClick={() => onAsk(label)}>{label}</button>;
      })}
    </div>
  );
}

// MARK's own robot face, for his messages and the page header.
function MarkFace({ size = 32 }) {
  return (
    <svg className="mk2-face" width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <rect x="4" y="8" width="24" height="18" rx="7" />
      <circle cx="12" cy="17" r="2.4" className="eye" />
      <circle cx="20" cy="17" r="2.4" className="eye" />
      <path d="M16 8V4.5" />
      <circle cx="16" cy="3.5" r="1.6" className="eye" />
    </svg>
  );
}

// The plan blocks: the #1 job with its steps, the backup, and a rule the trader can add.
// In a chat answer MARK's bold opening already says the #1 job, so the box only lists the steps.
function StepsBox({ steps }) {
  const { t } = useI18n();
  return (
    <div className="mk2-steps calm">
      <p className="mk2-label">{t('Steps for your next session')}</p>
      <ol>{steps.map((x, i) => <li key={i}><b>{i + 1}</b><span><Inline text={x} /></span></li>)}</ol>
    </div>
  );
}
// The backup: a label, then the title in bold and the text. `box` draws it as a card (Action plan).
function BackupBox({ title, text, box = false }) {
  const { t } = useI18n();
  const clean = String(title || '').replace(/^(backup|plan b|respaldo)\s*[,:·-]?\s*/i, '');
  const parts = clean.split(/:\s*/);
  const [lead, rest] = parts.length > 1 ? [parts[0], parts.slice(1).join(': ')] : ['', clean];
  const restCap = rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : rest;
  return (
    <div className={`mk2-backup${box ? ' box' : ' calm'}`}>
      <p className="mk2-label good">{t('Backup')}{lead ? ` · ${lead}` : ''}</p>
      <p>{restCap && <strong><Inline text={restCap} /> </strong>}<Inline text={text} /></p>
    </div>
  );
}
function RuleBox({ text, status, onRule }) {
  const { t } = useI18n();
  return (
    <div className={`mk2-rule${status === 'added' ? ' added' : ''}`}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" /></svg>
      <p><span>{status === 'added' ? t('Your rule') : t('Suggested rule')}</span>{text}</p>
      {onRule && (status === 'added' ? <em>✓ {t('In your rules')}</em> : (
        <span className="mk2-rule-actions">
          <button type="button" className="small" onClick={() => onRule(text, 'add')}>{t('Add to my rules')}</button>
          {status !== 'dismissed' && <button type="button" className="secondary small" onClick={() => onRule(text, 'dismiss')}>{t('Not now')}</button>}
        </span>
      ))}
    </div>
  );
}

function Reply({ text, data, onAsk, last, busy, ruleStatus, onRule }) {
  const { t } = useI18n();
  // Steps and the backup that follows them sit side by side on wide screens.
  const parts = parseReply(text).filter((p) => !(p.type === 'text' && !p.text.trim()));
  const merged = [];
  for (const p of parts) {
    const prev = merged[merged.length - 1];
    if (p.type === 'backup' && prev && prev.type === 'steps') merged[merged.length - 1] = { type: 'pair', steps: prev, backup: p };
    else merged.push(p);
  }
  return merged.map((part, i) => {
    if (part.type === 'pair') return <div key={i} className="mk2-pair"><StepsBox steps={part.steps.steps} /><BackupBox title={part.backup.title} text={part.backup.text} /></div>;
    if (part.type === 'text') return <Markdown key={i} text={part.text} />;
    if (part.type === 'cards') return <Cards key={i} cards={part.cards} />;
    if (part.type === 'chart') return <Chart key={i} spec={part.spec} data={data} />;
    if (part.type === 'steps') return <StepsBox key={i} steps={part.steps} />;
    if (part.type === 'backup') return <BackupBox key={i} title={part.title} text={part.text} />;
    if (part.type === 'rule') return <RuleBox key={i} text={part.text} status={ruleStatus(part.text)} onRule={onRule} />;
    if (part.type === 'next') return last ? <Next key={i} items={part.items} onAsk={onAsk} disabled={busy} /> : null;
    return <p key={i} className="mk-typing">{t('Drawing…')}</p>;
  });
}

// Asked once, before anything is sent: MARK runs on Anthropic's Claude, so the member's trading data
// goes to Anthropic to write each answer.
function Consent({ onAgree }) {
  const { t } = useI18n();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  return (
    <section className="mk-onboard mk2-consent">
      <p className="eyebrow">{t('Coach MARK')}</p>
      <h1 className="routine-title">{t('Before MARK reads your journal')}</h1>
      <p className="subtitle">{t('MARK is an AI coach that runs on Claude, made by Anthropic. To coach you, he needs to read your trading data.')}</p>
      <ul className="mk2-consent-list">
        <li>{t('Each time you ask MARK something or get a debrief, Market Matrix sends Anthropic what he needs to answer: your trades and results for the accounts you picked, your notes and reflections, your rules, and what MARK knows about you.')}</li>
        <li>{t('Anthropic uses it only to write MARK’s answer. Under Anthropic’s commercial terms, your data isn’t used to train its AI models.')}</li>
        <li>{t('Your conversations, debriefs and plan are saved in your Market Matrix account. You can clear a chat, remove anything MARK knows about you, or delete your account at any time.')}</li>
        <li>{t('MARK coaches your process. He never tells you what or when to trade, and his answers aren’t financial advice.')}</li>
      </ul>
      <label className="mk2-consent-check">
        <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
        <span>{t('I agree that Market Matrix sends my trading data to Anthropic to run MARK, as described in the')} <a href="/privacy" target="_blank" rel="noreferrer">{t('Privacy Policy')}</a>.</span>
      </label>
      <div className="mk-actions">
        <button type="button" disabled={!checked || saving} onClick={async () => { setSaving(true); try { await onAgree(); } finally { setSaving(false); } }}>{saving ? t('Saving…') : t('Continue')} &rarr;</button>
      </div>
    </section>
  );
}

function Onboarding({ initial, onSaved, onCancel }) {
  const { t } = useI18n();
  const [answers, setAnswers] = useState(() => ({ goal: '', ...initial }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const done = QUESTIONS.every(([key]) => answers[key]);
  async function save() {
    setSaving(true);
    try {
      onSaved(await api('/mark/profile', { method: 'PUT', body: answers }));
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }
  return (
    <section className="mk-onboard">
      <p className="eyebrow">{t('Coach MARK')}</p>
      <h1 className="routine-title">{t('Hi, I’m MARK, your trading coach.')}</h1>
      <p className="subtitle">{t('I read your whole journal and coach how you trade: your process, your rules and your habits. I never tell you what or when to trade. First, a few quick questions so I coach you the way you learn.')}</p>
      {QUESTIONS.map(([key, question, options]) => (
        <fieldset key={key} className="mk-question">
          <legend>{t(question)}</legend>
          <div className="mk-options">
            {options.map(([value, label]) => (
              <button key={value} type="button" className={`secondary small${answers[key] === value ? ' active' : ''}`} aria-pressed={answers[key] === value}
                onClick={() => setAnswers((a) => ({ ...a, [key]: value }))}>{t(label)}</button>
            ))}
          </div>
        </fieldset>
      ))}
      <label className="mk-question">
        <span className="mk-legend">{t('What do you want to get out of trading right now? (optional)')}</span>
        <textarea rows={2} maxLength={300} value={answers.goal || ''} onChange={(e) => setAnswers((a) => ({ ...a, goal: e.target.value }))}
          placeholder={t('e.g. pass my Topstep evaluation without blowing it on one bad day')} />
      </label>
      {error && <p className="error">{error}</p>}
      <div className="mk-actions">
        {onCancel && <button type="button" className="secondary" onClick={onCancel}>{t('Cancel')}</button>}
        <button type="button" disabled={!done || saving} onClick={save}>{saving ? t('Saving…') : t('Start coaching')} &rarr;</button>
      </div>
    </section>
  );
}

function Debrief({ d }) {
  const { t } = useI18n();
  const list = (title, items, cls) => (items && items.length ? <div className={`mk-list ${cls}`}><h4>{t(title)}</h4><ul>{items.map((x) => <li key={x}>{x}</li>)}</ul></div> : null);
  return (
    <div className="mk-debrief-body">
      <h3>{d.headline}</h3>
      <p>{d.summary}</p>
      {list('What went well', d.wentWell, 'good')}
      {list('What cost you', d.toFix, 'bad')}
      {list('Your rules', d.rules, '')}
      {d.focus && <p className="mk-focus"><strong>{t('Next session, focus on:')}</strong> {d.focus}</p>}
    </div>
  );
}

// The credits used this month as a ring (admins: used, no limit).
function CreditRing({ used, limit, admin, trial }) {
  const { t } = useI18n();
  const pct = admin ? 0 : Math.min(100, (used / limit) * 100);
  const C = 2 * Math.PI * 15;
  return (
    <div className={`mk2-credits${!admin && pct > 90 ? ' low' : ''}`} title={t('A quick question uses a few credits, a full review about {range}. Daily debriefs are free.', { range: REVIEW_RANGE })}>
      <svg viewBox="0 0 36 36" aria-hidden="true"><circle cx="18" cy="18" r="15" /><circle cx="18" cy="18" r="15" className="on" strokeDasharray={`${(pct / 100) * C} ${C}`} /></svg>
      <span><strong>{admin ? used.toLocaleString() : Math.max(0, limit - used).toLocaleString()}</strong>{admin ? t('credits used · no limit') : trial ? t('of {limit} trial credits left', { limit: limit.toLocaleString() }) : t('of {limit} credits left', { limit: limit.toLocaleString() })}</span>
    </div>
  );
}

const INTRO_KEY = 'mm-mark-intro-hidden';
const weekdayOf = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'long' });
// A note that starts "Early sign: …" or "Pattern: …" shows its lead in bold.
function noteLead(text) {
  const m = /^([^:]{3,28}):\s+(.*)$/s.exec(String(text));
  return m ? <><strong>{m[1]}:</strong> {m[2]}</> : text;
}

const TABS = [['chat', 'Chat with MARK'], ['findings', 'Findings'], ['plan', 'Action plan'], ['rules', 'Rules']];

export default function MarkPage({ user, trades, notes, accounts, allAccounts = accounts, selectedIds = null, onSelectAccounts, library, plays = [], scope, onSessionExpired }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [rules, setRules] = useState([]);
  const [editingStyle, setEditingStyle] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [debriefing, setDebriefing] = useState(false);
  const [debriefError, setDebriefError] = useState('');
  const [newNote, setNewNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [introHidden, setIntroHidden] = useState(() => {
    try {
      return localStorage.getItem(INTRO_KEY) === '1';
    } catch {
      return false;
    }
  });
  function hideIntro() {
    setIntroHidden(true);
    try {
      localStorage.setItem(INTRO_KEY, '1');
    } catch {
      // Not remembered in private mode.
    }
  }
  const [openNote, setOpenNote] = useState(null);
  const [asking, setAsking] = useState(null);
  const [tab, setTab] = useState('chat');
  const [history, setHistory] = useState(null);
  const [viewing, setViewing] = useState(null);
  const end = useRef(null);
  const box = useRef(null);
  const coachDay = useMemo(() => latestTradingDay(trades), [trades]);
  const [shownDay, setShownDay] = useState(null);
  const chartData = useMemo(() => ({ trades, accounts, plays, library }), [trades, accounts, plays, library]);
  // A question asked from Home's Ask MARK box: sent once MARK is ready to answer.
  const location = useLocation();
  const asked = useRef(false);

  useEffect(() => {
    api('/mark').then(setState).catch((err) => (err.status === 401 ? onSessionExpired() : setError(err.message)));
    api('/routine').then((r) => setRules(r.rules || [])).catch(() => {});
  }, [onSessionExpired]);
  // The chat scrolls inside its own box, so the page (and its header) stays put.
  useEffect(() => { if (box.current && tab === 'chat') box.current.scrollTop = box.current.scrollHeight; }, [state && state.messages.length, sending, tab, viewing]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (box.current && sending) box.current.scrollTop = box.current.scrollHeight; });

  // How each of the coached day's trades moved while open, from the trade chart's 1-minute bars
  // (TopstepX members only). Loaded once per trade.
  const moves = useRef({});
  async function loadMoves(day) {
    try {
      const charts = await api('/charts');
      if (!charts.available || !charts.connected || charts.locked) return;
      const todays = trades.filter((x) => tradingDayKey(x.date) === day && !x.timeUnknown && x.exitDate && !(x._id in moves.current)).slice(0, 10);
      await Promise.all(todays.map(async (x) => {
        const data = await api(`/trades/${x._id}/chart?unit=1`).catch(() => null);
        moves.current[x._id] = data && data.bars ? tradeMove(x, data.bars) : null;
      }));
    } catch {
      // Without prices MARK still coaches from the trades themselves.
    }
  }
  const snapshot = (day) => buildMarkSnapshot({ trades, notes, accounts, rules, library, day, scope, moves: moves.current });
  const debrief = state && (state.debriefs.find((d) => d.day === (shownDay || coachDay)) || null);
  const hasTrades = trades.some((x) => x.date);

  async function getDebrief(refresh = false) {
    setDebriefing(true);
    setDebriefError('');
    try {
      await loadMoves(coachDay);
      const d = await api('/mark/debrief', { method: 'POST', body: { day: coachDay, lang, refresh, snapshot: snapshot(coachDay) } });
      setState((s) => ({ ...s, memory: d.memory || s.memory, debriefs: [d, ...s.debriefs.filter((x) => x.day !== d.day)] }));
      setShownDay(coachDay);
    } catch (err) {
      setDebriefError(t(err.message));
    } finally {
      setDebriefing(false);
    }
  }

  // mode: 'quick' or 'review'. Without one, a big-sounding question asks first.
  async function send(text, mode) {
    const message = (text || draft).trim();
    if (!message || sending) return;
    if (!mode && soundsBig(message)) {
      setAsking(message);
      return;
    }
    setTab('chat');
    setViewing(null);
    setAsking(null);
    setDraft('');
    setSending(true);
    const kind = mode === 'review' ? 'review' : 'quick';
    setState((s) => ({ ...s, messages: [...s.messages, { role: 'user', text: message }, { role: 'assistant', text: '', pending: true, mode: kind }] }));
    const setReply = (reply, done, extra = {}) => setState((s) => {
      const list = [...s.messages];
      list[list.length - 1] = { ...list[list.length - 1], text: reply, pending: !done, ...extra };
      return { ...s, messages: list };
    });
    try {
      await loadMoves(coachDay);
      const res = await fetch('/api/mark/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-MM-Sid': sessionId().id },
        body: JSON.stringify({ message, mode: kind, lang, tz: Intl.DateTimeFormat().resolvedOptions().timeZone, snapshot: snapshot() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (res.status === 401) onSessionExpired();
        setReply(`⚠️ ${data.message ? t(data.message) : t('MARK couldn’t answer just now. Please try again in a minute.')}`, true);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let raw = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        raw += decoder.decode(value, { stream: true });
        setReply(raw.split(USAGE_MARK)[0], false);
      }
      // After the answer the server adds the credits it used, the saved message, the plan and
      // suggested rules it picked up, and for admins the cost.
      const [reply, tail] = raw.split(USAGE_MARK);
      let usage = null;
      try { usage = tail ? JSON.parse(tail) : null; } catch { usage = null; }
      setReply(reply, true, usage ? { credits: usage.credits, cost: usage.cost, id: usage.id } : {});
      setState((s) => ({
        ...s, usage: { ...s.usage, used: s.usage.used + ((usage && usage.credits) || 0) },
        ...(usage && 'plan' in usage ? { plan: usage.plan } : {}), ...(usage && usage.suggestions ? { suggestions: usage.suggestions } : {}),
      }));
    } catch {
      setReply(`⚠️ ${t('Could not reach the server. Check your connection and try again.')}`, true);
    } finally {
      setSending(false);
    }
  }

  async function saveMemory(memory) {
    try {
      const r = await api('/mark/memory', { method: 'PUT', body: { memory } });
      setState((s) => ({ ...s, memory: r.memory }));
    } catch (err) {
      setError(err.message);
    }
  }
  async function clearChat() {
    if (!window.confirm(t('Clear this chat? What MARK knows about you and your plan stay.'))) return;
    await api('/mark/messages', { method: 'DELETE' }).catch(() => {});
    setState((s) => ({ ...s, messages: [] }));
  }
  async function newChat() {
    if (!state.messages.length) return;
    await api('/mark/chats', { method: 'POST' }).catch(() => {});
    setState((s) => ({ ...s, messages: [], hasHistory: true }));
    setHistory(null);
    setViewing(null);
  }
  async function openHistory() {
    setViewing(null);
    setHistory('loading');
    try {
      setHistory(await api('/mark/chats'));
    } catch {
      setHistory([]);
    }
  }
  async function openChat(c) {
    try {
      setViewing({ ...c, messages: await api(`/mark/chats/${c.chat}`) });
      setHistory(null);
    } catch {
      // Stays on the list.
    }
  }
  async function vote(m, v) {
    const next = m.vote === v ? null : v;
    setState((s) => ({ ...s, messages: s.messages.map((x) => (x.id === m.id ? { ...x, vote: next } : x)) }));
    api(`/mark/messages/${m.id}/vote`, { method: 'PUT', body: { vote: next } }).catch(() => {});
  }
  async function onRule(text, action) {
    try {
      const r = await api('/mark/rules', { method: 'POST', body: { text, action } });
      setState((s) => ({ ...s, suggestions: r.suggestions }));
      if (r.rules) setRules(r.rules);
    } catch (err) {
      window.alert(t(err.message));
    }
  }
  const ruleStatus = (text) => {
    const x = (state.suggestions || []).find((y) => y.text.toLowerCase() === text.toLowerCase());
    if (rules.some((r) => r.trim().toLowerCase() === text.trim().toLowerCase())) return 'added';
    return x ? x.status : 'open';
  };
  async function savePlan(body) {
    try {
      const r = await api('/mark/plan', { method: 'PUT', body });
      setState((s) => ({ ...s, plan: r.plan }));
    } catch (err) {
      window.alert(t(err.message));
    }
  }

  const pendingAsk = location.state && location.state.ask;
  useEffect(() => {
    if (!pendingAsk || asked.current || !state || !state.consented || !state.onboarded || !state.ready) return;
    if (state.access && !state.access.ok && !user.isAdmin) return;
    asked.current = true;
    navigate('/app/mark', { replace: true, state: null });
    send(pendingAsk, 'quick');
  }, [pendingAsk, state]); // eslint-disable-line react-hooks/exhaustive-deps
  if (error) return <p className="error">{error}</p>;
  if (!state) return <p className="empty">{t('Loading…')}</p>;
  if (!state.consented) {
    return <Consent onAgree={async () => { await api('/mark/consent', { method: 'PUT' }); setState((s) => ({ ...s, consented: true })); }} />;
  }
  if (!state.onboarded || editingStyle) {
    return <Onboarding initial={state.style} onCancel={state.onboarded ? () => setEditingStyle(false) : null}
      onSaved={(r) => { setState((s) => ({ ...s, style: r.style, onboarded: true })); setEditingStyle(false); }} />;
  }

  const left = Math.max(0, state.usage.limit - state.usage.used);
  // Trial over (or no plan): what MARK wrote stays readable, but nothing new is asked.
  const locked = Boolean(state.access && !state.access.ok && !user.isAdmin);
  const trialEnds = state.access && state.access.trialEndsAt ? new Date(state.access.trialEndsAt).toLocaleDateString(currentLocale(), { weekday: 'long', month: 'long', day: 'numeric' }) : null;
  const canAsk = state.ready && !locked;
  const reviewMin = state.usage.reviewMin || 35;
  // A session waiting for its debrief.
  const ready = hasTrades && !state.debriefs.some((d) => d.day === coachDay);
  const shown = accounts.filter((a) => !a.archived);
  // The account chips: click to coach on that account, click again to drop it; several at once.
  const pickable = allAccounts.filter((a) => !a.archived);
  const toggleAccount = (id) => {
    const now = selectedIds || [];
    onSelectAccounts(now.includes(id) ? now.filter((x) => x !== id) : [...now, id]);
  };
  const openRules = (state.suggestions || []).filter((x) => x.status === 'open' && ruleStatus(x.text) === 'open');
  const shownMessages = viewing ? viewing.messages : state.messages;

  return (
    <div className="mk-page mk2">
      <header className="mk2-head">
        <div className="mk2-title">
          <span className="mk2-sparkle" aria-hidden="true">✦</span>
          <div>
            <h1>{t('Your AI trading coach')}</h1>
            <p>{t('MARK reads your trades, journal, rules and accounts, and coaches how you trade. Never what or when to trade.')}</p>
          </div>
        </div>
        <div className="mk2-head-side">
          <div className="mk2-accounts" role="group" aria-label={t('Which accounts MARK coaches on')}>
            <span className="mk2-accounts-label">{t('Coaching on')}</span>
            {onSelectAccounts ? (
              <>
                <button type="button" className={`mk2-chip${!selectedIds ? ' on' : ''}`} aria-pressed={!selectedIds} onClick={() => onSelectAccounts('all')}>{t('All')}</button>
                {pickable.map((a) => {
                  const on = Boolean(selectedIds && selectedIds.includes(a._id));
                  return <button key={a._id} type="button" className={`mk2-chip${on ? ' on' : ''}`} aria-pressed={on} onClick={() => toggleAccount(a._id)}>{a.name}</button>;
                })}
              </>
            ) : shown.map((a) => <span key={a._id} className="mk2-chip">{a.name}</span>)}
          </div>
        </div>
      </header>
      {!state.ready && <p className="analytics-warning">MARK isn’t switched on yet: add ANTHROPIC_API_KEY on Render (admin note).</p>}
      {locked && (
        <section className="pro-lock mk2-lock">
          <h2>{t(state.access.reason === 'trial-over' ? 'Your 4-day MARK trial is over' : 'Coach MARK is part of Pro')}</h2>
          <p>{t('Everything MARK wrote is still here. Upgrade to Pro to keep getting a debrief after every session, your action plan and 400 credits a month for questions.')}</p>
          <a href="/app/plans" className="cta-button">{t('See plans')} &rarr;</a>
        </section>
      )}
      {trialEnds && !locked && (
        <p className="mk2-trial-note">{t('You’re trying MARK free until {date}, with {n} credits. After that, Coach MARK is part of Pro.', { date: trialEnds, n: state.usage.limit })}</p>
      )}

      <div className="mk2-grid">
        <aside className="mk2-side">
          {!introHidden && (
            <div className="mk2-intro">
              <span className="mk2-sparkle small" aria-hidden="true">✦</span>
              <p><strong>{t('New here?')}</strong> {t('MARK reads your trades, journal and rules, and gives you one clear thing to work on next.')}</p>
              <button type="button" className="icon-button" aria-label={t('Close')} onClick={hideIntro}>&times;</button>
            </div>
          )}

          <section className="mk2-card mk2-debrief">
            <svg className="mk2-sunrise" viewBox="0 0 160 90" aria-hidden="true">
              <defs><radialGradient id="mk2-sun" cx="0.62" cy="0.62" r="0.5"><stop offset="0" stopColor="#ffe3a3" /><stop offset="0.25" stopColor="#f5b941" stopOpacity="0.85" /><stop offset="1" stopColor="#f5b941" stopOpacity="0" /></radialGradient></defs>
              <circle cx="100" cy="56" r="44" fill="url(#mk2-sun)" />
              <path d="M30 90 L78 44 L96 60 L116 36 L160 78 L160 90 Z" className="far" />
              <path d="M0 90 L52 58 L82 76 L112 54 L160 90 Z" className="near" />
            </svg>
            <div className="mk2-card-top">
              <span className="mk2-round" aria-hidden="true"><svg viewBox="0 0 24 24"><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4.5V3h6v1.5M9 10h6M9 14h6M9 18h4" /></svg></span>
              <div>
                <h2>{t('Daily Debrief')}{ready && <span className="mk2-ready">{t('Ready')}</span>}</h2>
                {hasTrades && <p className="mk2-date">{dayText(shownDay || coachDay)}</p>}
              </div>
            </div>
            <p className="mk2-debrief-text">{!hasTrades ? t('Your debriefs start once you have trades.')
              : ready ? t('Your {weekday} debrief is ready. Review your previous session and get clear takeaways.', { weekday: weekdayOf(coachDay) })
                : t('Review your previous session and get clear takeaways.')}</p>
            {state.debriefs.length > 1 && (
              <select className="mk2-day" value={shownDay || coachDay} onChange={(e) => setShownDay(e.target.value)} aria-label={t('Pick a day')}>
                {[...new Set([coachDay, ...state.debriefs.map((d) => d.day)])].map((d) => <option key={d} value={d}>{shortDay(d)}</option>)}
              </select>
            )}
            {debrief ? <Debrief d={debrief} /> : hasTrades && (shownDay || coachDay) === coachDay && (
              <>
                <button type="button" className="mk2-big" onClick={() => getDebrief(false)} disabled={debriefing || !canAsk}>
                  {debriefing ? t('MARK is reviewing your day…') : <>{t('Get My Debrief')} <span aria-hidden="true">&rarr;</span></>}
                </button>
                <p className="mk2-free">{t('Daily debriefs are free')}</p>
              </>
            )}
            {debrief && (shownDay || coachDay) === coachDay && user.isAdmin && (
              <button type="button" className="link-button" onClick={() => getDebrief(true)} disabled={debriefing || !canAsk}>
                {debriefing ? t('MARK is reviewing your day…') : t('Write it again with my latest notes')}
              </button>
            )}
            {debriefError && <p className="error">{debriefError}</p>}
          </section>

          {state.plan && <FocusCard plan={state.plan} onOpen={() => setTab('plan')} />}

          <section className="mk2-card mk2-knows">
            <div className="mk2-card-top">
              <span className="mk2-brain" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M9 4a3 3 0 0 0-3 3 3 3 0 0 0-2 5 3 3 0 0 0 2 5 3 3 0 0 0 3 3 2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" /><path d="M15 4a3 3 0 0 1 3 3 3 3 0 0 1 2 5 3 3 0 0 1-2 5 3 3 0 0 1-3 3 2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" /><path d="M6 12h2.5M15.5 12H18M8 8.5c1 0 1.5.5 1.5 1.5M16 15.5c-1 0-1.5-.5-1.5-1.5" /></svg></span>
              <div>
                <h2>{t('What MARK Knows About You')}</h2>
                <p>{t('Key takeaways from your recent trading.')}</p>
              </div>
            </div>
            {state.memory.length ? (
              <ul className="mk2-memory">
                {state.memory.map((m, i) => {
                  const kind = m.source === 'you' ? 'you' : /^(early sign|señal temprana)/i.test(m.text) ? 'early' : /^(pattern|patr[oó]n)/i.test(m.text) ? 'pattern' : 'note';
                  const isOpen = openNote === i;
                  return (
                    <li key={`${m.text}-${i}`} className={`${kind}${isOpen ? ' open' : ''}`}>
                      <button type="button" className="mk2-note" aria-expanded={isOpen} onClick={() => setOpenNote(isOpen ? null : i)}>
                        <span className={`mk2-note-ic ${kind}`} aria-hidden="true">
                          {kind === 'early' ? <svg viewBox="0 0 24 24"><path d="M4 17l6-6 4 4 6-7" /><path d="M15 8h5v5" /></svg>
                            : kind === 'pattern' ? <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="1" /></svg>
                              : kind === 'you' ? <svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" /><path d="M4 20c1.5-4 4.5-6 8-6s6.5 2 8 6" /></svg>
                                : <svg viewBox="0 0 24 24"><path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-4 10.5c.8.8 1 1.5 1 2.5h6c0-1 .2-1.7 1-2.5A6 6 0 0 0 12 3z" /></svg>}
                        </span>
                        <span className="mk2-note-text">{noteLead(m.text)}</span>
                        <svg className="mk2-chev" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
                      </button>
                      {isOpen && (
                        <div className="mk2-note-more">
                          <span>{m.source === 'you' ? t('You added this') : t('MARK noticed this')}{m.at ? ` · ${shortDay(String(m.at).slice(0, 10))}` : ''}</span>
                          <button type="button" className="link-button biz-delete" onClick={() => { setOpenNote(null); saveMemory(state.memory.filter((_, j) => j !== i)); }}>{t('Remove')}</button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : <p className="empty">{t('Nothing yet. Get your first debrief or chat with MARK.')}</p>}
            {adding ? (
              <form className="mk2-add" onSubmit={(e) => { e.preventDefault(); if (newNote.trim()) { saveMemory([...state.memory, { text: newNote.trim(), source: 'you' }]); setNewNote(''); setAdding(false); } }}>
                <input autoFocus value={newNote} onChange={(e) => setNewNote(e.target.value)} maxLength={300} placeholder={t('e.g. I trade the NY open only, 9:30 to 11:00')} />
                <button type="submit" className="small" disabled={!newNote.trim()}>{t('Add')}</button>
                <button type="button" className="secondary small" onClick={() => { setAdding(false); setNewNote(''); }}>{t('Cancel')}</button>
              </form>
            ) : (
              <button type="button" className="mk2-pill" onClick={() => setAdding(true)}><span aria-hidden="true">+</span> {t('Add a Note')}</button>
            )}
            <button type="button" className="mk2-gear-link" onClick={() => setEditingStyle(true)}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" /></svg>
              <span>{t('Change How MARK Coaches You')}</span>
            </button>
          </section>
        </aside>

        <section className="mk2-main">
          <nav className="mk2-tabs" aria-label={t('Coach MARK')}>
            {TABS.map(([id, label]) => (
              <button key={id} type="button" className={`mk2-tab${tab === id ? ' on' : ''}`} aria-pressed={tab === id} onClick={() => setTab(id)}>
                {t(label)}
                {id === 'rules' && openRules.length > 0 && <span className="mk2-count">{openRules.length}</span>}
                {id === 'plan' && state.plan && <span className="mk2-dot" aria-hidden="true" />}
              </button>
            ))}
          </nav>

          {tab === 'chat' && (
            <div className="mk2-panel mk2-chat">
              <div className="mk2-chat-bar">
                <div className="mk2-who">
                  <MarkFace size={36} />
                  <div><strong>MARK</strong><span>{viewing ? t('Earlier chat · {date}', { date: shortDay(String(viewing.at).slice(0, 10)) }) : t('Your AI trading coach')}</span></div>
                </div>
                <span className="mk2-chat-actions">
                  {viewing && <button type="button" className="link-button" onClick={() => setViewing(null)}>&larr; {t('Back to your chat')}</button>}
                  {(state.hasHistory || viewing) && <button type="button" className="link-button" onClick={openHistory}>{t('History')}</button>}
                  {!viewing && state.messages.length > 0 && <button type="button" className="link-button" onClick={newChat}>{t('New chat')}</button>}
                  <CreditRing used={state.usage.used} limit={state.usage.limit} admin={user.isAdmin} trial={state.usage.trial} />
                  {!viewing && state.messages.length > 0 && (
                    <button type="button" className="icon-button mk2-trash" aria-label={t('Clear chat')} title={t('Clear chat')} onClick={clearChat}>
                      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6" /></svg>
                    </button>
                  )}
                </span>
              </div>
              {history ? (
                <div className="mk2-history">
                  <div className="mk-card-head"><h3>{t('Earlier chats')}</h3><button type="button" className="icon-button" aria-label={t('Close')} onClick={() => setHistory(null)}>&times;</button></div>
                  {history === 'loading' ? <p className="empty">{t('Loading…')}</p> : !history.length ? <p className="empty">{t('No earlier chats yet.')}</p> : (
                    <ul>
                      {history.map((c) => (
                        <li key={c.chat}><button type="button" className="mk2-history-row" onClick={() => openChat(c)}>
                          <strong>{c.title}</strong><small>{shortDay(String(c.at).slice(0, 10))} · {t('{n} messages', { n: c.count })}</small>
                        </button></li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <div className="mk-messages" aria-live="polite" ref={box}>
                  {!shownMessages.length && (
                    <div className="mk-empty">
                      <MarkFace size={48} />
                      <p>{t('Ask me anything about how you trade. A few ideas:')}</p>
                    </div>
                  )}
                  {shownMessages.map((m, i) => (
                    <div key={m.id || i} className={`mk-msg ${m.role}${m.mode === 'review' ? ' review' : ''}`}>
                      {m.role === 'assistant' ? (
                        <>
                          <div className="mk2-from"><MarkFace size={26} /><span>MARK</span></div>
                          <div className="mk2-bubble">
                            {m.text ? <Reply text={m.text} data={chartData} onAsk={(q) => send(q)} last={!viewing && i === shownMessages.length - 1} busy={sending} ruleStatus={ruleStatus} onRule={viewing ? null : onRule} />
                              : <p className="mk-typing">{m.mode === 'review' ? t('MARK is reading your whole journal…') : t('MARK is thinking…')}</p>}
                          </div>
                          {!m.pending && (m.credits > 0 || m.id) && (
                            <p className="mk-used">
                              {m.credits > 0 && (m.mode === 'review' ? t('Full review · used {n} credits', { n: m.credits }) : t(m.credits === 1 ? 'Used 1 credit' : 'Used {n} credits', { n: m.credits }))}
                              {m.cost && <span className="admin-preview-badge mk-cost">{`$${m.cost.dollars.toFixed(3)} · ${m.cost.input.toLocaleString()} in · ${m.cost.cacheRead.toLocaleString()} cached · ${m.cost.output.toLocaleString()} out`}</span>}
                              {m.id && !viewing && (
                                <span className="mk2-votes">
                                  <button type="button" className={`icon-button${m.vote === 'up' ? ' on' : ''}`} aria-label={t('Helpful')} aria-pressed={m.vote === 'up'} onClick={() => vote(m, 'up')}>👍</button>
                                  <button type="button" className={`icon-button${m.vote === 'down' ? ' on' : ''}`} aria-label={t('Not helpful')} aria-pressed={m.vote === 'down'} onClick={() => vote(m, 'down')}>👎</button>
                                </span>
                              )}
                            </p>
                          )}
                        </>
                      ) : <p>{m.text}</p>}
                    </div>
                  ))}
                  <div ref={end} />
                </div>
              )}
              {!viewing && !history && (
                <>
                  {asking && (
                    <div className="mk-ask" role="dialog" aria-label={t('How should MARK answer?')}>
                      <p><strong>{t('This sounds like a big question.')}</strong> {t('A full review reads your whole journal and gives you a scorecard, comparisons, charts and a plan. It uses about {range} credits.', { range: REVIEW_RANGE })}
                        {!user.isAdmin && <> {t('You have {n} credits left this month.', { n: left.toLocaleString() })}</>}</p>
                      <div className="mk-ask-actions">
                        <button type="button" disabled={!user.isAdmin && left < reviewMin} onClick={() => send(asking, 'review')}>{t('Full review · about {range} credits', { range: REVIEW_RANGE })}</button>
                        <button type="button" className="secondary" onClick={() => send(asking, 'quick')}>{t('Quick answer · a few credits')}</button>
                        <button type="button" className="link-button" onClick={() => setAsking(null)}>{t('Cancel')}</button>
                      </div>
                    </div>
                  )}
                  <div className="mk2-starters">
                    {STARTERS.map((x) => <button key={x} type="button" className="secondary small" disabled={!canAsk || sending} onClick={() => send(t(x))}>{t(x)}</button>)}
                  </div>
                  <div className="mk2-compose-row">
                  <form className="mk2-compose" onSubmit={(e) => { e.preventDefault(); send(); }}>
                    <span className="mk2-sparkle small" aria-hidden="true">✦</span>
                    <textarea rows={1} value={draft} maxLength={4000} disabled={!canAsk}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder={t('Ask MARK about your trading…')} />
                    <button type="submit" className="mk2-send" aria-label={t('Send')} disabled={sending || !draft.trim() || !canAsk || (!user.isAdmin && left <= 0)}>
                      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12l16-8-6 16-2-6z" /></svg>
                    </button>
                  </form>
                  <button type="button" className="secondary mk2-review" disabled={sending || !canAsk} title={t('A full review of your whole journal. Uses about {range} credits.', { range: REVIEW_RANGE })}
                    onClick={() => setAsking(draft.trim() || t('Give me a full review of my trading: everything you can see.'))}>
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5M10 13h6M10 17h6" /></svg>
                    {t('Full Review')} <small>{REVIEW_RANGE} cr</small>
                  </button>
                  </div>
                  <div className="mk2-compose-foot">
                    <p className="field-hint">{t('MARK coaches your process. He doesn’t give trade ideas or financial advice.')}</p>
                    <p className="field-hint">{t('Quick question = a few credits')} · {t('Debriefs free')}{state.messages.length > 0 && <> · <button type="button" className="link-button" onClick={clearChat}>{t('Clear chat')}</button></>}</p>
                  </div>
                </>
              )}
            </div>
          )}

          {tab === 'findings' && <Findings state={state} onPick={(d) => { setShownDay(d); }} />}
          {tab === 'plan' && <ActionPlan plan={state.plan} onSave={savePlan} onAsk={() => send(t('What should I work on in my next session?'), 'quick')} busy={sending || !state.ready} />}
          {tab === 'rules' && <RulesTab suggestions={state.suggestions || []} rules={rules} ruleStatus={ruleStatus} onRule={onRule} onEdit={() => navigate('/app/morning')} />}
        </section>
      </div>
    </div>
  );
}

// The action plan at a glance: the #1 job and how often the rule held in the last 7 days.
function FocusCard({ plan, onOpen }) {
  const { t } = useI18n();
  const today = currentTradingDay();
  const weekAgo = new Date(`${today}T12:00:00Z`);
  weekAgo.setUTCDate(weekAgo.getUTCDate() - 6);
  const from = weekAgo.toISOString().slice(0, 10);
  const week = (plan.checks || []).filter((c) => c.day >= from && (c.held === true || c.held === false));
  const held = week.filter((c) => c.held).length;
  const pct = week.length ? Math.round((held / week.length) * 100) : 0;
  return (
    <section className="mk2-card mk2-focus">
      <div className="mk2-card-top">
        <span className="mk2-round small" aria-hidden="true"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="1" /></svg></span>
        <div><h2>{t('Coaching Focus')}<span className="mk2-week">{t('This week')}</span></h2></div>
      </div>
      <p className="mk2-focus-job">{plan.job}</p>
      {plan.rule && <p className="mk2-focus-rule">{plan.rule}</p>}
      <p className="mk2-focus-stat">{week.length ? t('Rule followed: {held} of {n} sessions ({pct}%)', { held, n: week.length, pct }) : t('Mark each session in your Action plan to see how the rule holds.')}</p>
      <div className="jo-progress-track"><span style={{ width: `${pct}%` }} /></div>
      <button type="button" className="link-button" onClick={onOpen}>{t('Open your Action plan')} &rarr;</button>
    </section>
  );
}

// Findings: what MARK has noticed over time (his notes) and every debrief he has written.
function Findings({ state }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(null);
  const noticed = state.memory.filter((m) => m.source === 'mark').slice().reverse();
  return (
    <div className="mk2-panel">
      <section className="mk2-sub">
        <h3>{t('Patterns MARK noticed')}</h3>
        <p className="field-hint">{t('What MARK has learned about how you trade, newest first. Remove anything that’s wrong on the left.')}</p>
        {noticed.length ? (
          <ul className="mk2-findings">
            {noticed.map((m, i) => <li key={i}><span>{m.text}</span>{m.at && <small>{shortDay(String(m.at).slice(0, 10))}</small>}</li>)}
          </ul>
        ) : <p className="empty">{t('Nothing yet. MARK notes patterns after each debrief.')}</p>}
      </section>
      <section className="mk2-sub">
        <h3>{t('Your debriefs')}</h3>
        {state.debriefs.length ? (
          <ul className="mk2-debriefs">
            {state.debriefs.map((d) => (
              <li key={d.day}>
                <button type="button" className="mk2-debrief-row" aria-expanded={open === d.day} onClick={() => setOpen(open === d.day ? null : d.day)}>
                  <small>{shortDay(d.day)}</small><strong>{d.headline}</strong>{d.focus && <span>{t('Focus:')} {d.focus}</span>}
                </button>
                {open === d.day && <Debrief d={d} />}
              </li>
            ))}
          </ul>
        ) : <p className="empty">{t('Your debriefs show here once MARK has written one.')}</p>}
      </section>
    </div>
  );
}

// The action plan from MARK's latest steps, with today's check-in and how the rule has held.
function ActionPlan({ plan, onSave, onAsk, busy }) {
  const { t } = useI18n();
  const today = currentTradingDay();
  if (!plan) {
    return (
      <div className="mk2-panel mk2-empty-panel">
        <h3>{t('No action plan yet')}</h3>
        <p>{t('Ask MARK what to work on. His steps become your plan here, and you check them off each session.')}</p>
        <button type="button" onClick={onAsk} disabled={busy}>{t('What should I work on in my next session?')}</button>
      </div>
    );
  }
  const check = (plan.checks || []).find((c) => c.day === today) || { day: today, done: [] };
  const done = plan.steps.map((_, i) => Boolean(check.done && check.done[i]));
  const recent = (plan.checks || []).slice(-14);
  // Days in a row the rule held, newest first; a day not marked yet (like today) doesn't break it.
  const marks = (plan.checks || []).filter((c) => c.held === true || c.held === false);
  let streak = 0;
  for (let i = marks.length - 1; i >= 0 && marks[i].held === true; i -= 1) streak += 1;
  const held = (plan.checks || []).filter((c) => c.held === true).length;
  const marked = (plan.checks || []).filter((c) => c.held === true || c.held === false).length;
  return (
    <div className="mk2-panel">
      <div className="mk2-steps mk2-plan">
        <p className="mk2-job"><span>{t('Your #1 job')}</span>{plan.job}</p>
        <p className="field-hint">{t('Since {date}', { date: shortDay(String(plan.at).slice(0, 10)) })}</p>
        <h4>{t('Today’s steps')} <small>{dayText(today)}</small></h4>
        <ol className="mk2-checks">
          {plan.steps.map((x, i) => (
            <li key={i}>
              <label><input type="checkbox" checked={done[i]} onChange={() => onSave({ day: today, done: done.map((v, j) => (j === i ? !v : v)), held: check.held })} />
                <span><Inline text={x} /></span></label>
            </li>
          ))}
        </ol>
      </div>
      {plan.rule && (
        <div className="mk2-rule added mk2-held">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" /></svg>
          <p><span>{t('Did the rule hold today?')}</span>{plan.rule}</p>
          <span className="mk2-rule-actions">
            <button type="button" className={`small${check.held === true ? '' : ' secondary'}`} aria-pressed={check.held === true} onClick={() => onSave({ day: today, done, held: true })}>{t('Yes')}</button>
            <button type="button" className={`small${check.held === false ? ' danger' : ' secondary'}`} aria-pressed={check.held === false} onClick={() => onSave({ day: today, done, held: false })}>{t('No')}</button>
          </span>
        </div>
      )}
      {marked > 0 && (
        <div className="mk2-streak">
          {streak > 0 && <strong>{t(streak === 1 ? '{n} day in a row' : '{n} days in a row', { n: streak })}</strong>}
          <span>{t('The rule held on {held} of {n} days you marked.', { held, n: marked })}</span>
          <span className="mk2-dots" aria-hidden="true">{recent.map((c) => <i key={c.day} className={c.held === true ? 'held' : c.held === false ? 'broke' : ''} title={c.day} />)}</span>
        </div>
      )}
      {plan.backup && <BackupBox title={plan.backupTitle || t('Next, once #1 holds for a week')} text={plan.backup} box />}
      <p className="mk2-plan-foot">
        <button type="button" className="secondary small" onClick={onAsk} disabled={busy}>{t('Ask MARK how it’s going')}</button>
        <button type="button" className="link-button" onClick={() => { if (window.confirm(t('Clear this plan? MARK will make a new one the next time you ask what to work on.'))) onSave({ clear: true }); }}>{t('Clear plan')}</button>
      </p>
    </div>
  );
}

// Rules: the ones MARK suggested (add or not now) and the trader's own rules.
function RulesTab({ suggestions, rules, ruleStatus, onRule, onEdit }) {
  const { t } = useI18n();
  const open = suggestions.filter((x) => ruleStatus(x.text) === 'open');
  const later = suggestions.filter((x) => ruleStatus(x.text) === 'dismissed');
  const fromMark = new Set(suggestions.filter((x) => ruleStatus(x.text) === 'added').map((x) => x.text.toLowerCase()));
  return (
    <div className="mk2-panel">
      <section className="mk2-sub">
        <h3>{t('Suggested by MARK')}</h3>
        {open.length ? open.map((x) => <RuleBox key={x.text} text={x.text} status="open" onRule={onRule} />)
          : <p className="empty">{t('No new suggestions. When MARK suggests a rule in a chat, it waits here until you decide.')}</p>}
      </section>
      <section className="mk2-sub">
        <div className="mk-card-head"><h3>{t('Your rules')}</h3><button type="button" className="link-button" onClick={onEdit}>{t('Edit them in Morning prep')} &rarr;</button></div>
        {rules.length ? (
          <ol className="mk2-rules-list">
            {rules.map((r) => <li key={r}><span>{r}</span>{fromMark.has(r.toLowerCase()) && <em className="mk2-from-mark">{t('from MARK')}</em>}</li>)}
          </ol>
        ) : <p className="empty">{t('No rules yet.')}</p>}
      </section>
      {later.length > 0 && (
        <section className="mk2-sub">
          <h3>{t('Set aside')}</h3>
          {later.map((x) => <RuleBox key={x.text} text={x.text} status="dismissed" onRule={onRule} />)}
        </section>
      )}
    </div>
  );
}
